// Voxel GI — world-space global illumination with multi-bounce light propagation
// Engine code: no game knowledge.
//
// Pipeline:
//   1. Game submits axis-aligned boxes (position, size, color)
//   2. Voxelization compute: fills 3D texture with albedo+opacity
//   3. Light injection compute: applies direct lighting to occupied voxels
//   4. Mipmap generation: coarser mip levels for sampling
//   5. Bounce passes: propagate indirect light through the volume (N iterations)
//   6. Lighting pass: samples the volume for ambient color + reflections

import type { GPU } from "./gpu.js";

// --- Voxelization compute shader ---
const VOXELIZE_SHADER = /* wgsl */ `
struct VoxelParams {
  gridMin: vec3f,  _pad0: f32,
  gridMax: vec3f,  mediumCeil: f32,
  gridSize: vec3u, boxCount: u32,
  mediumColor: vec3f, mediumAlpha: f32,
};

struct Box {
  min: vec3f, emissive: f32,
  max: vec3f, _pad1: f32,
  color: vec3f, _pad2: f32,
};

@group(0) @binding(0) var voxelOut: texture_storage_3d<rgba8unorm, write>;
@group(0) @binding(1) var<uniform> params: VoxelParams;
@group(0) @binding(2) var<storage, read> boxes: array<Box>;

// Translucent volume color/alpha from uniforms (e.g. water tint below sea level)

@compute @workgroup_size(4, 4, 4)
fn voxelizeMain(@builtin(global_invocation_id) gid: vec3u) {
  if (gid.x >= params.gridSize.x || gid.y >= params.gridSize.y || gid.z >= params.gridSize.z) { return; }

  // Voxel center in world space
  let voxelSize = (params.gridMax - params.gridMin) / vec3f(params.gridSize);
  let worldPos = params.gridMin + (vec3f(gid) + 0.5) * voxelSize;

  // Check all boxes
  var color = vec3f(0.0);
  var hit = false;

  for (var i = 0u; i < params.boxCount; i++) {
    let b = boxes[i];
    if (worldPos.x >= b.min.x && worldPos.x <= b.max.x &&
        worldPos.y >= b.min.y && worldPos.y <= b.max.y &&
        worldPos.z >= b.min.z && worldPos.z <= b.max.z) {
      color = b.color;
      hit = true;
      break;
    }
  }

  if (hit) {
    textureStore(voxelOut, gid, vec4f(color, 1.0));
  } else if (params.mediumCeil > params.gridMin.y && worldPos.y < params.mediumCeil) {
    // Below medium ceiling and not inside solid geometry: translucent volume
    textureStore(voxelOut, gid, vec4f(params.mediumColor, params.mediumAlpha));
  } else {
    textureStore(voxelOut, gid, vec4f(0.0, 0.0, 0.0, 0.0));
  }
}
`;

// --- Light injection compute shader ---
const MAX_POINT_LIGHTS = 16;

const LIGHT_INJECT_SHADER = /* wgsl */ `
const MAX_PL = 16u;

struct PointLight {
  pos: vec3f, radius: f32,
  color: vec3f, intensity: f32,
};

struct LightParams {
  gridMin: vec3f, _pad0: f32,
  gridMax: vec3f, _pad1: f32,
  gridSize: vec3u, boxCount: u32,
  lightDir: vec3f, pointLightCount: u32,
  pointLights: array<PointLight, 16>,
};

struct Box {
  min: vec3f, emissive: f32,
  max: vec3f, _pad1: f32,
  color: vec3f, _pad2: f32,
};

@group(0) @binding(0) var voxelIn: texture_3d<f32>;
@group(0) @binding(1) var litOut: texture_storage_3d<rgba16float, write>;
@group(0) @binding(2) var<uniform> params: LightParams;
@group(0) @binding(3) var<storage, read> boxes: array<Box>;

fn estimateNormal(coord: vec3i, size: vec3i) -> vec3f {
  var n = vec3f(0.0);
  let cx = clamp(coord.x, 1, size.x - 2);
  let cy = clamp(coord.y, 1, size.y - 2);
  let cz = clamp(coord.z, 1, size.z - 2);

  n.x = textureLoad(voxelIn, vec3i(cx-1, cy, cz), 0).a - textureLoad(voxelIn, vec3i(cx+1, cy, cz), 0).a;
  n.y = textureLoad(voxelIn, vec3i(cx, cy-1, cz), 0).a - textureLoad(voxelIn, vec3i(cx, cy+1, cz), 0).a;
  n.z = textureLoad(voxelIn, vec3i(cx, cy, cz-1), 0).a - textureLoad(voxelIn, vec3i(cx, cy, cz+1), 0).a;

  let len = length(n);
  if (len > 0.001) { return n / len; }
  return vec3f(0.0, 1.0, 0.0);
}

@compute @workgroup_size(4, 4, 4)
fn lightInjectMain(@builtin(global_invocation_id) gid: vec3u) {
  let size = vec3i(params.gridSize);
  if (i32(gid.x) >= size.x || i32(gid.y) >= size.y || i32(gid.z) >= size.z) { return; }

  let voxel = textureLoad(voxelIn, vec3i(gid), 0);
  if (voxel.a < 0.01) {
    textureStore(litOut, gid, vec4f(0.0));
    return;
  }

  // Water voxels (fractional alpha < 1.0): pass through tint, no direct lighting
  if (voxel.a < 0.9) {
    textureStore(litOut, gid, vec4f(voxel.rgb, voxel.a));
    return;
  }

  // Check if this voxel is inside an emissive box
  let voxelSizeV = (params.gridMax - params.gridMin) / vec3f(params.gridSize);
  let worldPos = params.gridMin + (vec3f(gid) + 0.5) * voxelSizeV;

  var emissiveIntensity = 0.0;
  for (var i = 0u; i < params.boxCount; i++) {
    let b = boxes[i];
    if (b.emissive > 0.0 &&
        worldPos.x >= b.min.x && worldPos.x <= b.max.x &&
        worldPos.y >= b.min.y && worldPos.y <= b.max.y &&
        worldPos.z >= b.min.z && worldPos.z <= b.max.z) {
      emissiveIntensity = b.emissive;
      break;
    }
  }

  if (emissiveIntensity > 0.0) {
    textureStore(litOut, gid, vec4f(voxel.rgb * emissiveIntensity, voxel.a));
    return;
  }

  let N = estimateNormal(vec3i(gid), size);

  // Sun direct lighting
  let L = normalize(params.lightDir);
  let ndl = max(dot(N, L), 0.0);
  var lit = voxel.rgb * (0.3 + 0.7 * ndl);

  // Point light contribution
  for (var p = 0u; p < params.pointLightCount; p++) {
    let pl = params.pointLights[p];
    let toLight = pl.pos - worldPos;
    let dist = length(toLight);
    if (dist > pl.radius) { continue; }
    let Lp = toLight / dist;
    let ndlp = max(dot(N, Lp), 0.0);
    let atten = 1.0 - (dist / pl.radius);
    let falloff = atten * atten;
    lit += voxel.rgb * pl.color * pl.intensity * ndlp * falloff;
  }

  textureStore(litOut, gid, vec4f(lit, voxel.a));
}
`;

// --- Mipmap generation compute shader ---
const MIPMAP_SHADER = /* wgsl */ `
@group(0) @binding(0) var srcMip: texture_3d<f32>;
@group(0) @binding(1) var dstMip: texture_storage_3d<rgba16float, write>;

@compute @workgroup_size(4, 4, 4)
fn mipmapMain(@builtin(global_invocation_id) gid: vec3u) {
  let dstSize = vec3i(textureDimensions(dstMip));
  if (i32(gid.x) >= dstSize.x || i32(gid.y) >= dstSize.y || i32(gid.z) >= dstSize.z) { return; }

  // Average 2×2×2 block from source
  let src = vec3i(gid) * 2;
  var color = vec4f(0.0);
  for (var dz = 0; dz < 2; dz++) {
    for (var dy = 0; dy < 2; dy++) {
      for (var dx = 0; dx < 2; dx++) {
        color += textureLoad(srcMip, src + vec3i(dx, dy, dz), 0);
      }
    }
  }
  color /= 8.0;

  textureStore(dstMip, gid, color);
}
`;

// --- Light bounce compute shader ---
// For each occupied voxel, sample incoming indirect light from 6 directions,
// multiply by surface albedo → write bounced light to a temp volume.
const BOUNCE_SHADER = /* wgsl */ `
struct BounceParams {
  gridMin: vec3f, _pad0: f32,
  gridMax: vec3f, _pad1: f32,
  gridSize: vec3u, _pad2: u32,
};

@group(0) @binding(0) var voxelAlbedo: texture_3d<f32>;  // raw surface colors
@group(0) @binding(1) var litVolume: texture_3d<f32>;     // current lit state (mipmapped)
@group(0) @binding(2) var litSampler: sampler;
@group(0) @binding(3) var bounceOut: texture_storage_3d<rgba16float, write>;
@group(0) @binding(4) var<uniform> params: BounceParams;

fn gridToUV(coord: vec3f) -> vec3f {
  return (coord + 0.5) / vec3f(params.gridSize);
}

// 6 axis-aligned directions for light gathering
const DIRS = array<vec3i, 6>(
  vec3i( 1, 0, 0), vec3i(-1, 0, 0),
  vec3i( 0, 1, 0), vec3i( 0,-1, 0),
  vec3i( 0, 0, 1), vec3i( 0, 0,-1),
);

@compute @workgroup_size(4, 4, 4)
fn bounceMain(@builtin(global_invocation_id) gid: vec3u) {
  let size = vec3i(params.gridSize);
  if (i32(gid.x) >= size.x || i32(gid.y) >= size.y || i32(gid.z) >= size.z) { return; }

  let albedo = textureLoad(voxelAlbedo, vec3i(gid), 0);
  if (albedo.a < 0.9) {
    // Empty or translucent: no bounce contribution
    textureStore(bounceOut, gid, vec4f(0.0));
    return;
  }

  // Sample incoming light from 6 directions at mip 2 (smooth, 4-voxel area)
  var incoming = vec3f(0.0);
  for (var i = 0; i < 6; i++) {
    let neighborCoord = vec3i(gid) + DIRS[i] * 4;
    let uv = gridToUV(vec3f(neighborCoord));
    if (all(uv >= vec3f(0.0)) && all(uv <= vec3f(1.0))) {
      let s = textureSampleLevel(litVolume, litSampler, uv, 2.0);
      incoming += s.rgb;
    }
  }
  incoming /= 6.0;

  // Bounced light = incoming × surface albedo
  let bounced = incoming * albedo.rgb;
  textureStore(bounceOut, gid, vec4f(bounced, albedo.a));
}
`;

// --- Accumulate bounce into litVolume ---
const ACCUMULATE_SHADER = /* wgsl */ `
@group(0) @binding(0) var litIn: texture_3d<f32>;
@group(0) @binding(1) var bounceIn: texture_3d<f32>;
@group(0) @binding(2) var litOut: texture_storage_3d<rgba16float, write>;

@compute @workgroup_size(4, 4, 4)
fn accumulateMain(@builtin(global_invocation_id) gid: vec3u) {
  let size = textureDimensions(litIn);
  if (gid.x >= size.x || gid.y >= size.y || gid.z >= size.z) { return; }

  let existing = textureLoad(litIn, vec3i(gid), 0);
  let bounce = textureLoad(bounceIn, vec3i(gid), 0);

  // Add bounced light, keep original alpha
  textureStore(litOut, gid, vec4f(existing.rgb + bounce.rgb, existing.a));
}
`;

// Max boxes per frame
const MAX_BOXES = 256;

export type VoxelBounds = {
  min: [number, number, number];
  max: [number, number, number];
};

export class VoxelGI {
  /** 3D texture with lit voxels — bind in lighting pass for cone tracing */
  litVolume: GPUTexture;
  volumeSampler: GPUSampler;

  // Grid config
  private gridSize: [number, number, number] = [256, 128, 256];
  private bounds: VoxelBounds = { min: [-10, 0, -10], max: [10, 20, 10] };

  // Textures
  private voxelAlbedo: GPUTexture; // raw voxelized albedo + opacity
  private mipLevels: number;

  // Pipelines
  private voxelizePipeline: GPUComputePipeline;
  private lightInjectPipeline: GPUComputePipeline;
  private mipmapPipeline: GPUComputePipeline;

  // Bind groups
  private voxelizeBindGroup!: GPUBindGroup;
  private lightInjectBindGroup!: GPUBindGroup;
  private mipmapBindGroups: GPUBindGroup[] = [];

  // Buffers
  private voxelParamsBuffer: GPUBuffer;
  private lightParamsBuffer: GPUBuffer;
  private boxBuffer: GPUBuffer;

  // Box submission
  private boxData = new Float32Array(MAX_BOXES * 12); // 12 floats per box
  private boxCount = 0;

  // GI cache — skip recompute when scene is static
  private _dirty = true;
  private lastBoxHash = 0;

  // Layouts (needed for bind group recreation)
  private voxelizeLayout: GPUBindGroupLayout;
  private lightInjectLayout: GPUBindGroupLayout;
  private mipmapLayout: GPUBindGroupLayout;

  // Bounce passes
  private bouncePipeline: GPUComputePipeline;
  private accumulatePipeline: GPUComputePipeline;
  private bounceVolume: GPUTexture; // temp: receives bounced light
  private litVolumeCopy: GPUTexture; // temp: copy of litVolume mip 0 for read during accumulate
  private bounceLayout: GPUBindGroupLayout;
  private accumulateLayout: GPUBindGroupLayout;
  private bounceBindGroup!: GPUBindGroup;
  private accumulateBindGroup!: GPUBindGroup;
  private bounceParamsBuffer: GPUBuffer;

  constructor(private gpu: GPU) {
    const { device } = gpu;

    // Calculate mip levels
    const maxDim = Math.max(...this.gridSize);
    this.mipLevels = Math.floor(Math.log2(maxDim)) + 1;

    // Create 3D textures
    this.voxelAlbedo = this.create3DTexture("rgba8unorm", 1);
    this.litVolume = this.create3DTexture("rgba16float", this.mipLevels);
    this.volumeSampler = device.createSampler({
      magFilter: "linear",
      minFilter: "linear",
      mipmapFilter: "linear",
    });

    // Buffers
    this.voxelParamsBuffer = device.createBuffer({ size: 64, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
    // 64 bytes header + 16 point lights × 32 bytes = 576 bytes
    this.lightParamsBuffer = device.createBuffer({ size: 576, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
    this.boxBuffer = device.createBuffer({ size: MAX_BOXES * 48, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST });

    // --- Voxelize pipeline ---
    const voxModule = device.createShaderModule({ code: VOXELIZE_SHADER });
    this.voxelizeLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, storageTexture: { access: "write-only", format: "rgba8unorm", viewDimension: "3d" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
      ],
    });
    this.voxelizePipeline = device.createComputePipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.voxelizeLayout] }),
      compute: { module: voxModule, entryPoint: "voxelizeMain" },
    });

    // --- Light injection pipeline ---
    const lightModule = device.createShaderModule({ code: LIGHT_INJECT_SHADER });
    this.lightInjectLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: "float", viewDimension: "3d" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, storageTexture: { access: "write-only", format: "rgba16float", viewDimension: "3d" } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
      ],
    });
    this.lightInjectPipeline = device.createComputePipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.lightInjectLayout] }),
      compute: { module: lightModule, entryPoint: "lightInjectMain" },
    });

    // --- Mipmap pipeline ---
    const mipModule = device.createShaderModule({ code: MIPMAP_SHADER });
    this.mipmapLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: "float", viewDimension: "3d" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, storageTexture: { access: "write-only", format: "rgba16float", viewDimension: "3d" } },
      ],
    });
    this.mipmapPipeline = device.createComputePipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.mipmapLayout] }),
      compute: { module: mipModule, entryPoint: "mipmapMain" },
    });

    // --- Bounce pipeline ---
    this.bounceVolume = this.create3DTexture("rgba16float", 1);
    this.litVolumeCopy = this.create3DTexture("rgba16float", 1);
    this.bounceParamsBuffer = device.createBuffer({ size: 48, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });

    const bounceModule = device.createShaderModule({ code: BOUNCE_SHADER });
    this.bounceLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: "float", viewDimension: "3d" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: "float", viewDimension: "3d" } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, sampler: {} },
        { binding: 3, visibility: GPUShaderStage.COMPUTE, storageTexture: { access: "write-only", format: "rgba16float", viewDimension: "3d" } },
        { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
      ],
    });
    this.bouncePipeline = device.createComputePipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.bounceLayout] }),
      compute: { module: bounceModule, entryPoint: "bounceMain" },
    });

    // --- Accumulate pipeline ---
    const accModule = device.createShaderModule({ code: ACCUMULATE_SHADER });
    this.accumulateLayout = device.createBindGroupLayout({
      entries: [
        { binding: 0, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: "float", viewDimension: "3d" } },
        { binding: 1, visibility: GPUShaderStage.COMPUTE, texture: { sampleType: "float", viewDimension: "3d" } },
        { binding: 2, visibility: GPUShaderStage.COMPUTE, storageTexture: { access: "write-only", format: "rgba16float", viewDimension: "3d" } },
      ],
    });
    this.accumulatePipeline = device.createComputePipeline({
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.accumulateLayout] }),
      compute: { module: accModule, entryPoint: "accumulateMain" },
    });

    this.rebuildBindGroups();
  }

  private create3DTexture(format: GPUTextureFormat, mipLevels: number): GPUTexture {
    return this.gpu.device.createTexture({
      size: this.gridSize,
      format,
      dimension: "3d",
      mipLevelCount: mipLevels,
      usage: GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST | GPUTextureUsage.COPY_SRC,
    });
  }

  private rebuildBindGroups(): void {
    const { device } = this.gpu;

    this.voxelizeBindGroup = device.createBindGroup({
      layout: this.voxelizeLayout,
      entries: [
        { binding: 0, resource: this.voxelAlbedo.createView({ dimension: "3d" }) },
        { binding: 1, resource: { buffer: this.voxelParamsBuffer } },
        { binding: 2, resource: { buffer: this.boxBuffer } },
      ],
    });

    this.lightInjectBindGroup = device.createBindGroup({
      layout: this.lightInjectLayout,
      entries: [
        { binding: 0, resource: this.voxelAlbedo.createView({ dimension: "3d" }) },
        { binding: 1, resource: this.litVolume.createView({ dimension: "3d", baseMipLevel: 0, mipLevelCount: 1 }) },
        { binding: 2, resource: { buffer: this.lightParamsBuffer } },
        { binding: 3, resource: { buffer: this.boxBuffer } },
      ],
    });

    // Mipmap bind groups: one per mip level transition
    this.mipmapBindGroups = [];
    for (let i = 1; i < this.mipLevels; i++) {
      this.mipmapBindGroups.push(device.createBindGroup({
        layout: this.mipmapLayout,
        entries: [
          { binding: 0, resource: this.litVolume.createView({ dimension: "3d", baseMipLevel: i - 1, mipLevelCount: 1 }) },
          { binding: 1, resource: this.litVolume.createView({ dimension: "3d", baseMipLevel: i, mipLevelCount: 1 }) },
        ],
      }));
    }

    // Bounce: reads albedo + litVolume (mipmapped), writes to bounceVolume
    this.bounceBindGroup = device.createBindGroup({
      layout: this.bounceLayout,
      entries: [
        { binding: 0, resource: this.voxelAlbedo.createView({ dimension: "3d" }) },
        { binding: 1, resource: this.litVolume.createView({ dimension: "3d" }) },
        { binding: 2, resource: this.volumeSampler },
        { binding: 3, resource: this.bounceVolume.createView({ dimension: "3d" }) },
        { binding: 4, resource: { buffer: this.bounceParamsBuffer } },
      ],
    });

    // Accumulate: reads litVolumeCopy + bounceVolume, writes to litVolume mip 0
    this.accumulateBindGroup = device.createBindGroup({
      layout: this.accumulateLayout,
      entries: [
        { binding: 0, resource: this.litVolumeCopy.createView({ dimension: "3d" }) },
        { binding: 1, resource: this.bounceVolume.createView({ dimension: "3d" }) },
        { binding: 2, resource: this.litVolume.createView({ dimension: "3d", baseMipLevel: 0, mipLevelCount: 1 }) },
      ],
    });
  }

  configure(bounds: VoxelBounds): void {
    this.bounds = bounds;
    this._dirty = true;
  }

  /** Mark GI as needing recompute (call when scene geometry changes) */
  markDirty(): void { this._dirty = true; }

  beginFrame(): void {
    this.boxCount = 0;
  }

  addBox(
    x: number, y: number, z: number,
    w: number, h: number, d: number,
    r: number, g: number, b: number,
    emissive = 0
  ): void {
    if (this.boxCount >= MAX_BOXES) return;
    const i = this.boxCount * 12;
    this.boxData[i] = x - w / 2;
    this.boxData[i + 1] = y - h / 2;
    this.boxData[i + 2] = z - d / 2;
    this.boxData[i + 3] = emissive;
    this.boxData[i + 4] = x + w / 2;
    this.boxData[i + 5] = y + h / 2;
    this.boxData[i + 6] = z + d / 2;
    this.boxData[i + 7] = 0;
    this.boxData[i + 8] = r;
    this.boxData[i + 9] = g;
    this.boxData[i + 10] = b;
    this.boxData[i + 11] = 0;
    this.boxCount++;
  }

  addEmissive(
    x: number, y: number, z: number,
    w: number, h: number, d: number,
    r: number, g: number, b: number,
    intensity = 1.0
  ): void {
    this.addBox(x, y, z, w, h, d, r, g, b, intensity);
  }

  /** Number of indirect light bounces (0 = direct only, 2 = editor default, 4 = game) */
  bounceCount = 2;

  /** Y level below which voxels become translucent (e.g. water surface). Set to -Infinity to disable. */
  mediumCeil = -Infinity;
  /** Color tint of the translucent volume */
  mediumColor: [number, number, number] = [0.06, 0.18, 0.14];
  /** Opacity of each medium voxel (0–1) */
  mediumAlpha = 0.25;

  compute(encoder: GPUCommandEncoder, lightDir: [number, number, number], pointLights?: Array<{ pos: [number,number,number]; radius: number; color: [number,number,number]; intensity: number }>): void {
    // Quick hash of box data to detect changes without deep comparison
    // Hash every float (not every 3rd) so vertical movement is detected
    let hash = this.boxCount * 31;
    const end = this.boxCount * 12;
    for (let i = 0; i < end; i++) hash = (hash * 31 + (this.boxData[i] * 1000 | 0)) | 0;
    if (hash !== this.lastBoxHash) { this._dirty = true; this.lastBoxHash = hash; }

    // Skip recompute if nothing changed (static scene optimization)
    if (!this._dirty) return;
    this._dirty = false;

    const { device } = this.gpu;
    const [gx, gy, gz] = this.gridSize;

    // Upload box data
    device.queue.writeBuffer(this.boxBuffer, 0, this.boxData, 0, this.boxCount * 12);

    // Voxel params (64 bytes = 16 floats)
    const vpInt = new ArrayBuffer(64);
    const vpF = new Float32Array(vpInt);
    const vpU = new Uint32Array(vpInt);
    vpF[0] = this.bounds.min[0]; vpF[1] = this.bounds.min[1]; vpF[2] = this.bounds.min[2];
    vpF[4] = this.bounds.max[0]; vpF[5] = this.bounds.max[1]; vpF[6] = this.bounds.max[2]; vpF[7] = this.mediumCeil;
    vpU[8] = gx; vpU[9] = gy; vpU[10] = gz; vpU[11] = this.boxCount;
    vpF[12] = this.mediumColor[0]; vpF[13] = this.mediumColor[1]; vpF[14] = this.mediumColor[2]; vpF[15] = this.mediumAlpha;
    device.queue.writeBuffer(this.voxelParamsBuffer, 0, vpInt);

    // Light params: 16 floats header + 16 lights × 8 floats = 144 floats = 576 bytes
    const lights = pointLights ?? [];
    const plCount = Math.min(lights.length, MAX_POINT_LIGHTS);
    const lpBuf = new ArrayBuffer(576);
    const lpF = new Float32Array(lpBuf);
    const lpU = new Uint32Array(lpBuf);
    lpF[0] = this.bounds.min[0]; lpF[1] = this.bounds.min[1]; lpF[2] = this.bounds.min[2];
    lpF[4] = this.bounds.max[0]; lpF[5] = this.bounds.max[1]; lpF[6] = this.bounds.max[2];
    lpU[8] = gx; lpU[9] = gy; lpU[10] = gz; lpU[11] = this.boxCount;
    lpF[12] = lightDir[0]; lpF[13] = lightDir[1]; lpF[14] = lightDir[2]; lpU[15] = plCount;
    // Point lights at offset 16 (each: pos(3) + radius(1) + color(3) + intensity(1) = 8 floats)
    for (let i = 0; i < plCount; i++) {
      const pl = lights[i];
      const o = 16 + i * 8;
      lpF[o] = pl.pos[0]; lpF[o+1] = pl.pos[1]; lpF[o+2] = pl.pos[2]; lpF[o+3] = pl.radius;
      lpF[o+4] = pl.color[0]; lpF[o+5] = pl.color[1]; lpF[o+6] = pl.color[2]; lpF[o+7] = pl.intensity;
    }
    device.queue.writeBuffer(this.lightParamsBuffer, 0, lpBuf);

    const wgX = Math.ceil(gx / 4);
    const wgY = Math.ceil(gy / 4);
    const wgZ = Math.ceil(gz / 4);

    // 1. Voxelize
    const voxPass = encoder.beginComputePass();
    voxPass.setPipeline(this.voxelizePipeline);
    voxPass.setBindGroup(0, this.voxelizeBindGroup);
    voxPass.dispatchWorkgroups(wgX, wgY, wgZ);
    voxPass.end();

    // 2. Light injection
    const lightPass = encoder.beginComputePass();
    lightPass.setPipeline(this.lightInjectPipeline);
    lightPass.setBindGroup(0, this.lightInjectBindGroup);
    lightPass.dispatchWorkgroups(wgX, wgY, wgZ);
    lightPass.end();

    // 3. Generate mipmaps
    this.generateMipmaps(encoder, gx, gy, gz);

    // 4. Bounce passes — propagate indirect light through the volume
    if (this.bounceCount > 0) {
      // Upload bounce params once
      const bpBuf = new ArrayBuffer(48);
      const bpF = new Float32Array(bpBuf);
      const bpU = new Uint32Array(bpBuf);
      bpF[0] = this.bounds.min[0]; bpF[1] = this.bounds.min[1]; bpF[2] = this.bounds.min[2];
      bpF[4] = this.bounds.max[0]; bpF[5] = this.bounds.max[1]; bpF[6] = this.bounds.max[2];
      bpU[8] = gx; bpU[9] = gy; bpU[10] = gz;
      device.queue.writeBuffer(this.bounceParamsBuffer, 0, bpBuf);

      for (let bounce = 0; bounce < this.bounceCount; bounce++) {
        // a. Compute bounce: read litVolume (mipmapped) → write bounceVolume
        const bp = encoder.beginComputePass();
        bp.setPipeline(this.bouncePipeline);
        bp.setBindGroup(0, this.bounceBindGroup);
        bp.dispatchWorkgroups(wgX, wgY, wgZ);
        bp.end();

        // b. Copy litVolume mip 0 → litVolumeCopy (so we can read while writing)
        encoder.copyTextureToTexture(
          { texture: this.litVolume, mipLevel: 0 },
          { texture: this.litVolumeCopy },
          this.gridSize,
        );

        // c. Accumulate: litVolumeCopy + bounceVolume → litVolume mip 0
        const ap = encoder.beginComputePass();
        ap.setPipeline(this.accumulatePipeline);
        ap.setBindGroup(0, this.accumulateBindGroup);
        ap.dispatchWorkgroups(wgX, wgY, wgZ);
        ap.end();

        // d. Regenerate mipmaps for next bounce
        this.generateMipmaps(encoder, gx, gy, gz);
      }
    }
  }

  private generateMipmaps(encoder: GPUCommandEncoder, gx: number, gy: number, gz: number): void {
    let mipW = gx >> 1, mipH = gy >> 1, mipD = gz >> 1;
    for (let i = 0; i < this.mipmapBindGroups.length && mipW >= 1 && mipH >= 1 && mipD >= 1; i++) {
      const mipPass = encoder.beginComputePass();
      mipPass.setPipeline(this.mipmapPipeline);
      mipPass.setBindGroup(0, this.mipmapBindGroups[i]);
      mipPass.dispatchWorkgroups(Math.ceil(mipW / 4), Math.ceil(mipH / 4), Math.ceil(mipD / 4));
      mipPass.end();
      mipW >>= 1; mipH >>= 1; mipD >>= 1;
    }
  }
}
